import { Controller } from "@hotwired/stimulus"

/**
 * UT Equipment Controller
 * Manages equipment selection for UT reports with single/multiple selection modes
 * Each card (Instrument, Probe, Calibration Block, Reference Block) has its own instance
 */
export default class extends Controller {
  static targets = ["container", "select", "hiddenFields"]
  static values = {
    type: Number,      // Equipment type ID (1=Instrument, 2=Probe, 3=Cal Block, 4=Ref Block)
    single: Boolean    // true for single selection (Instrument), false for multiple
  }
  static outlets = ["autosave"]

  connect() {
    this.equipmentData = new Map()
    this.loadExistingEquipment()
  }

  /**
   * Load existing equipment from the rendered table
   */
  loadExistingEquipment() {
    const rows = this.containerTarget.querySelectorAll('tr[data-equipment-id]')
    rows.forEach(row => {
      const equipmentId = row.dataset.equipmentId
      const cells = row.querySelectorAll('td')

      this.equipmentData.set(equipmentId, {
        id: equipmentId,
        name: cells[0]?.textContent?.trim() || '',
        maker: cells[1]?.textContent?.trim() || '',
        serialNo: cells[2]?.textContent?.trim() || ''
      })
    })
  }

  /**
   * Add equipment from the dropdown selection
   */
  async addEquipment(event) {
    event.preventDefault()

    const selectElement = this.selectTarget
    const equipmentId = selectElement.value

    if (!equipmentId) {
      alert('Please select equipment first')
      return
    }

    // Check if already added
    if (this.equipmentData.has(equipmentId)) {
      alert('This equipment is already added')
      this.resetSelect()
      return
    }

    // For single selection, check if one already exists
    if (this.singleValue && this.equipmentData.size > 0) {
      alert('Only one item allowed. Remove the existing one first.')
      this.resetSelect()
      return
    }

    try {
      // Fetch equipment data
      const response = await fetch(`/equipment/${equipmentId}.json`)
      if (!response.ok) throw new Error('Failed to fetch equipment data')

      const data = await response.json()

      // Add to map
      this.equipmentData.set(equipmentId, {
        id: equipmentId,
        name: data.report_text_or_name || data.name || '',
        maker: data.maker || '',
        serialNo: data.serial_no || ''
      })

      // Update UI
      this.renderEquipment()
      this.updateHiddenFields()
      this.resetSelect()

      // Dispatch event for other controllers
      document.dispatchEvent(new CustomEvent('ut-equipment:added', {
        detail: {
          equipmentId: equipmentId,
          equipmentTypeId: this.typeValue,
          equipmentData: data
        }
      }))

      // Trigger autosave
      await this.triggerAutosave()

    } catch (error) {
      console.error('[ut-equipment] Error adding equipment:', error)
      alert('Failed to add equipment. Please try again.')
    }
  }

  /**
   * Remove equipment
   */
  async removeEquipment(event) {
    event.preventDefault()

    const button = event.currentTarget
    const equipmentId = button.dataset.equipmentId

    if (!equipmentId) return

    // Get data before removing
    const removedData = this.equipmentData.get(equipmentId)

    // Remove from map
    this.equipmentData.delete(equipmentId)

    // Update UI
    this.renderEquipment()
    this.updateHiddenFields()

    // Dispatch event
    document.dispatchEvent(new CustomEvent('ut-equipment:removed', {
      detail: {
        equipmentId: equipmentId,
        equipmentTypeId: this.typeValue,
        equipmentData: removedData
      }
    }))

    // Trigger autosave
    await this.triggerAutosave()
  }

  /**
   * Clear all equipment (used by set selector)
   */
  clearAll() {
    this.equipmentData.clear()
    this.renderEquipment()
    this.updateHiddenFields()
  }

  /**
   * Add equipment directly (used by set selector, bypasses validation)
   */
  addEquipmentDirect(equipmentData) {
    if (this.singleValue && this.equipmentData.size > 0) {
      this.equipmentData.clear()
    }

    const id = equipmentData.id.toString()
    const data = {
      id: id,
      name: equipmentData.name || '',
      maker: equipmentData.maker || '',
      serialNo: equipmentData.serial_no || ''
    }
    this.equipmentData.set(id, data)
  }

  /**
   * Finish batch update (render + update hidden fields)
   */
  finishUpdate() {
    this.renderEquipment()
    this.updateHiddenFields()
  }

  /**
   * Render the equipment table
   */
  renderEquipment() {
    if (this.equipmentData.size === 0) {
      const icon = this.getIconForType()
      const label = this.getLabelForType()
      this.containerTarget.innerHTML = `
        <div class="text-muted text-center py-3">
          <i class="fa ${icon} mr-2"></i>
          No ${label} added yet
        </div>
      `
      this.updateBadge()
      return
    }

    let html = `
      <div class="table-responsive">
        <table class="table table-sm table-hover">
          <thead>
            <tr>
              <th>Name</th>
              <th>Maker</th>
              <th>Serial No.</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
    `

    this.equipmentData.forEach((eq, id) => {
      html += `
        <tr data-equipment-id="${id}">
          <td>${this.escapeHtml(eq.name)}</td>
          <td>${this.escapeHtml(eq.maker)}</td>
          <td>${this.escapeHtml(eq.serialNo)}</td>
          <td>
            <button class="btn btn-sm btn-link text-danger p-0" type="button"
                    data-action="click->ut-equipment#removeEquipment"
                    data-equipment-id="${id}"
                    title="Remove">
              <i class="fa fa-trash"></i>
            </button>
          </td>
        </tr>
      `
    })

    html += `
          </tbody>
        </table>
      </div>
    `

    this.containerTarget.innerHTML = html
    this.updateBadge()
  }

  /**
   * Update the badge count in card header
   */
  updateBadge() {
    const badge = this.element.querySelector('.badge')
    if (badge) {
      badge.textContent = this.equipmentData.size
    }
  }

  /**
   * Update hidden fields for form submission
   */
  updateHiddenFields() {
    let html = ''
    this.equipmentData.forEach((eq, id) => {
      html += `<input type="hidden" name="report[equipment_ids][]" value="${id}" />\n`
    })
    this.hiddenFieldsTarget.innerHTML = html
  }

  /**
   * Reset the select dropdown
   */
  resetSelect() {
    this.selectTarget.value = ''
    // Reset Select2 if present
    if (window.jQuery && window.jQuery(this.selectTarget).data('select2')) {
      window.jQuery(this.selectTarget).val('').trigger('change')
    }
  }

  /**
   * Get icon class for equipment type
   */
  getIconForType() {
    const icons = {
      1: 'fa-microchip',
      2: 'fa-broadcast-tower',
      3: 'fa-cube',
      4: 'fa-shapes'
    }
    return icons[this.typeValue] || 'fa-wrench'
  }

  /**
   * Get label for equipment type
   */
  getLabelForType() {
    const labels = {
      1: 'UT Instrument',
      2: 'UT Probes',
      3: 'Calibration Blocks',
      4: 'Reference Blocks'
    }
    return labels[this.typeValue] || 'Equipment'
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }

  /**
   * Trigger autosave via outlet
   */
  async triggerAutosave() {
    if (this.hasAutosaveOutlet) {
      await this.autosaveOutlet.saveNow()
    }
  }
}
